import { FC, useState } from "react";
import { FingerPrintView } from "../../features/views/FingerPrintView/FingerPrintView";
import { CustomFingerprint, LinkIcon } from "../../features/icons/Icons";
import { Button, Col, Modal, Row } from "react-bootstrap";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { PDFExport } from "../../modules/fingerprint-steps/fingerprint-narrative/PDFExport";
import fingerprintImg from "../../assets/media/fingerprint.svg";
import valuesDark from "../../assets/media/333.png";
import logo from "../../assets/media/logo.svg";
import { Loader } from "../Loader/Loader";
import { PDFExportSimplified } from "../../modules/fingerprint-steps/fingerprint-narrative/PDFExportSimplified";
import { ShareToLinkedIn } from "../ShareToLinkedIn/ShareToLinkedIn";

type Props = {
  userName: string; // first name + last name
  licenseCode: string;
  valueDrivers: string[];
  archetype: string;
  archetypeExplanation: string;
  valuePhilosophy: string;
  triggers: string[];
  triggersExplanation: string[];
  isLoading: boolean;
  isOpenFingerPrintModal: boolean;
};

export const FingerPrint: FC<Props> = ({
  userName,
  licenseCode,
  valueDrivers,
  archetype,
  archetypeExplanation,
  valuePhilosophy,
  triggers,
  triggersExplanation,
  isLoading,
  isOpenFingerPrintModal,
}) => {
  const [showShareLink, setShowShareLink] = useState<boolean>(false);
  const [showPDFVersionModal, setShowPDFVersionModal] = useState(false);

  return (
    <>
    <FingerPrintView background="navy" className="no-shapes stretch">
      <div className="final-fingerprint color-navy">
        <section className="fingerprint">
          <div
            className="title-card"
            style={{ position: "relative", top: -35 }}
          >
            <h1>Your Leadership Fingerprint™</h1>
          </div>
          <div className="fingerprint-wrapper" id="fingerprint-svg">
            <span className="name">{userName}</span>

            <div className="fingerprint-inner-wrapper">
              <div className="fingerprint-line" />

              <ul className="list-of-values">
                {isLoading ? (
                  <div style={{ marginLeft: 10, verticalAlign: "middle" }}>
                    <Loader /> <span>Loading...</span>
                  </div>
                ) : (
                  valueDrivers?.map((v) => {
                    return <li key={v}>{v}</li>;
                  })
                )}
              </ul>
            </div>

            <div className="fingerprint-svg">
              <div className="fingerprint-svg-bg" />

              <CustomFingerprint />

              <img src={fingerprintImg} className="fingerprint-bg" alt="" />
            </div>
          </div>
        </section>
      </div>

      <div
        style={{
          position: "relative",
          top: 110,
          display: "flex",
          gap: 10,
          alignItems: "center",
        }}
      >
        <ShareToLinkedIn
          licenseCode={licenseCode}
          isOpen={isOpenFingerPrintModal}
        />
      </div>

      <section className="final-narrative">
        <section
          className="fingerprint"
          style={{ marginTop: "15rem", maxWidth: "70%" }}
        >
          <div className="row">
            <div className="title-card">
              <h1>Values Philosophy</h1>
            </div>

            {isLoading ? (
              <div>
                <Loader />
                <span style={{ marginLeft: 10, verticalAlign: "middle" }}>
                  Loading...
                </span>
              </div>
            ) : (
              valuePhilosophy || (
                <div>
                  <p>
                    <span style={{ color: "yellow" }}>&#9888;</span>{" "}
                    Unfortunatelly, we could not generate your results because
                    of the technical issue.
                  </p>

                  <p>Here's what you can do to solve the issue:</p>

                  <p>
                    1. Try to re-generate the results:
                    <Button
                      type="primary"
                      onClick={() => window.location.reload()}
                      style={{ marginLeft: 15 }}
                    >
                      Generate results
                    </Button>
                  </p>

                  <p>
                    2. Contact our support team lapin@lapininternational.com and
                    we will send you your Digital Fingerprint.
                  </p>
                </div>
              )
            )}
          </div>

          <div className="row">
            <div className="title-card">
              <h1>Your Defensive Archetype</h1>
            </div>
            Your Leadership Fingerprint™ represents who you are at your best. We
            all experience moments when we don't show up as our ideal selves.
            This is where understanding your Defensive Archetype becomes
            essential. Your Defensive Archetype describes how you behave when
            triggered into a defensive state. By identifying these behaviors and
            recognizing what triggers them, you can learn to manage and master
            your reactions. This awareness helps you align more closely with
            your values system and more consistently present your best self.
          </div>

          <div className="row">
            <h3>Archetype:</h3>

            <p>{archetype}</p>
          </div>

          <div className="row">
            <h3>Archetype Explanation:</h3>

            <p>{archetypeExplanation}</p>
          </div>

          <div className="row">
            <h3>Triggers:</h3>

            <p>{triggers?.join(", ")}</p>
          </div>

          <div className="row">
            <h3>Triggers Explanation:</h3>

            {triggers?.map((t: any) => {
              return (
                <p key={t}>
                  <b>{t}:</b> {triggersExplanation?.[t]}
                </p>
              );
            })}
          </div>
        </section>

        <Row className="final-narrative-button-row">
          <Col>
            {valuePhilosophy && (
              <>
                <Button
                  onClick={() => setShowPDFVersionModal(true)}
                  className="btn-white no-wrap"
                >
                  Download now!
                </Button>

                <Modal
                  centered
                  show={showPDFVersionModal}
                  onHide={() => setShowPDFVersionModal(false)}
                >
                  <Modal.Title>
                    Please, choose the version of your PDF:
                  </Modal.Title>

                  <Modal.Footer>
                    <div
                      style={{
                        display: "flex",
                        gap: 10,
                      }}
                    >
                      <PDFDownloadLink
                        document={
                          <PDFExport
                            userName={userName}
                            valueDrivers={valueDrivers}
                            valuePhilosophy={valuePhilosophy}
                            archetype={archetype}
                            archetypeExplanation={archetypeExplanation}
                            triggers={triggers}
                            triggersExplanation={triggersExplanation}
                          />
                        }
                        fileName="Your_Fingerprint.pdf"
                        className="btn-primary no-wrap"
                        style={{ textDecoration: "none" }}
                      >
                        {({ blob, url, loading, error }) =>
                          loading ? "Loading document..." : "Colored PDF"
                        }
                      </PDFDownloadLink>

                      <PDFDownloadLink
                        document={
                          <PDFExportSimplified
                            userName={userName}
                            valueDrivers={valueDrivers}
                            valuePhilosophy={valuePhilosophy}
                            archetype={archetype}
                            archetypeExplanation={archetypeExplanation}
                            triggers={triggers}
                            triggersExplanation={triggersExplanation}
                          />
                        }
                        fileName="Your_Fingerprint.pdf"
                        className="btn-primary no-wrap"
                        style={{ textDecoration: "none" }}
                      >
                        {({ blob, url, loading, error }) =>
                          loading ? "Loading document..." : "Printable PDF"
                        }
                      </PDFDownloadLink>
                    </div>
                  </Modal.Footer>
                </Modal>
              </>
            )}
          </Col>

          <Col>
            <Button
              style={{ width: "100%" }}
              onClick={() => {
                setShowShareLink(!showShareLink);
              }}
              type="submit"
              className="btn-borderless"
            >
              Get Your Link
              <LinkIcon />
            </Button>

            {showShareLink && (
              <p
                style={{
                  width: "100%",
                  textAlign: "center",
                  color: "white",
                }}
                className="standard"
              >
                {`${window.location.origin.toString()}/digital-fingerprint/${licenseCode}`}
              </p>
            )}
          </Col>
        </Row>
      </section>
    </FingerPrintView>

    {/* LinkedIn share card — zero-height wrapper hides it visually while keeping it rendered */}
    <div aria-hidden="true" style={{ overflow: "hidden", height: 0, width: 0 }}>
      <div
        id="linkedin-share-card"
        className="final-fingerprint color-navy"
        style={{
          width: "1200px",
          backgroundColor: "#1a1a42",
          overflow: "hidden",
          padding: "48px 64px 24px",
          boxSizing: "border-box",
        }}
      >
        {/* Force SVG stroke to match the page's CSS override, which html-to-image skips for presentation attributes */}
        <style>{`#linkedin-share-card .fingerprint-wrapper svg path { stroke: #33337f !important; }`}</style>

        <img src={logo} alt="Lapin" style={{ width: 80, marginBottom: 16 }} />

        <section className="fingerprint">
          <div className="title-card">
            <h1>Your Leadership Fingerprint™</h1>
          </div>

          <div className="fingerprint-wrapper" style={{ marginLeft: "-64px" }}>
            <span className="name" style={{ position: "relative", top: 0, left: "64px", zIndex: 10, color: "white" }}>{userName}</span>

            <div className="fingerprint-inner-wrapper">
              <div className="fingerprint-line" />

              <ul className="list-of-values">
                {valueDrivers?.map((v) => (
                  <li key={v}>{v}</li>
                ))}
              </ul>
            </div>

            <div className="fingerprint-svg">
              <div className="fingerprint-svg-bg" />
              <CustomFingerprint />
              <img src={fingerprintImg} className="fingerprint-bg" alt="" />
            </div>
          </div>
        </section>
      </div>
    </div>
    </>
  );
};
